import { memo } from 'react';

import { ChevronDownIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { AgentSelectionItemsDropdown } from '~/components/v2/components/private-selection-bar/agent-selection-bar/AgentSelectionItemsDropdown';

export type AgentSelectionBarItemCountProps = {
  /** "5 items", "3 canvases", "All 1,200 items". */
  countLabel: string;
  /** Where the selection came from, when that is worth naming on its own. */
  scopeLabel?: string;
  selectedItemCount: number;
  showSelectionDropdown: boolean;
  hideScopeLabel?: boolean;
  itemCountMinMeasureRef?: (node: HTMLElement | null) => void;
  scopeMeasureRef?: (node: HTMLElement | null) => void;
};

/**
 * One dropdown trigger: count · scope ▾ when scope is shown, or count ▾ when scope is tucked.
 * Scope compresses (`flex-1` + truncate) while mounted; tuck hides it via CSS so layout
 * measurement stays stable. Chevron sits after scope when both are visible.
 */
export const AgentSelectionBarItemCount = memo(
  ({
    countLabel,
    hideScopeLabel = false,
    itemCountMinMeasureRef,
    scopeLabel,
    scopeMeasureRef,
    selectedItemCount,
    showSelectionDropdown,
  }: AgentSelectionBarItemCountProps) => {
    const showScopeVisually = !hideScopeLabel && !!scopeLabel;
    const showChevron = showSelectionDropdown && selectedItemCount > 1;
    const showChevronOnScope = showScopeVisually && showChevron;
    const showChevronOnCount = !showScopeVisually && showChevron;
    const scopeAccessibleName = scopeLabel ? `${countLabel}, ${scopeLabel}` : undefined;

    const tuckedScopeMeasure =
      !showScopeVisually && scopeLabel ? (
        <span
          ref={scopeMeasureRef}
          aria-hidden
          className="pointer-events-none absolute size-0 overflow-hidden opacity-0"
          data-selection-bar-scope-measure="tucked"
        >
          <span className="text-white/40">·</span>
          <span>{scopeLabel}</span>
        </span>
      ) : null;

    const summary = (
      <>
        <span
          ref={itemCountMinMeasureRef}
          className={cn(
            showScopeVisually
              ? 'shrink-0 font-semibold text-white tabular-nums'
              : 'inline-flex shrink-0 items-baseline gap-1.5',
          )}
        >
          {showScopeVisually ? (
            countLabel
          ) : (
            <>
              <span className="shrink-0 font-semibold text-white tabular-nums">{countLabel}</span>
              {showChevronOnCount && (
                <ChevronDownIcon
                  size="sm"
                  className="shrink-0 self-center text-white group-data-[state=open]:rotate-180"
                />
              )}
            </>
          )}
        </span>
        {showScopeVisually && (
          <span
            ref={scopeMeasureRef}
            aria-hidden={showSelectionDropdown ? true : undefined}
            className="flex min-w-0 flex-1 items-baseline gap-1.5 overflow-hidden"
          >
            <span className="shrink-0 text-white/40">·</span>
            <span className="min-w-0 truncate font-medium text-white/90">{scopeLabel}</span>
            {showChevronOnScope && (
              <ChevronDownIcon
                size="sm"
                className="shrink-0 self-center text-white group-data-[state=open]:rotate-180"
              />
            )}
          </span>
        )}
      </>
    );

    if (!showSelectionDropdown || selectedItemCount <= 1) {
      return (
        <div className="relative inline-flex min-w-0 max-w-full">
          {tuckedScopeMeasure}
          {hideScopeLabel && scopeLabel ? <span className="sr-only">{scopeAccessibleName}</span> : null}
          <div
            className="inline-flex min-w-0 max-w-full items-baseline gap-1.5 whitespace-nowrap text-14 leading-5"
            data-testid="SELECTION_ACTION_BAR_ITEMS_SELECTED"
          >
            {summary}
          </div>
        </div>
      );
    }

    return (
      <div className="relative inline-flex min-w-0 max-w-full">
        {tuckedScopeMeasure}
        <AgentSelectionItemsDropdown
          selectedItemCount={selectedItemCount}
          trigger={
            <button
              aria-label={scopeAccessibleName}
              className={cn(
                'group inline-flex min-w-0 max-w-full items-baseline gap-1.5 text-left text-14 leading-5',
                selectedItemCount > 1 && 'cursor-pointer',
              )}
              data-testid="SELECTION_ACTION_BAR_ITEMS_SELECTED"
              type="button"
            >
              {summary}
            </button>
          }
        />
      </div>
    );
  },
);

AgentSelectionBarItemCount.displayName = 'AgentSelectionBarItemCount';
